/**
 * Job Snapshot Manager — downloads and stores complete "offline packs" for jobs.
 * 
 * A snapshot includes:
 * - Full job details
 * - Appraisal templates/schema (vehicle parts list, questions)
 * - Existing appraisals/drafts for that job
 * - Customer data needed by appraisal screens
 * 
 * Stored in IndexedDB via offlineDb jobs_cache + a new job_snapshots store.
 */

import { supabase } from "@/integrations/supabase/client";
import { jobsCache, appState, describeDbError, isRecoverableDbError, type CachedJob } from "./offlineDb";
import { captureEvent } from "@/lib/observability";

// Rate-limited internal diagnostic for snapshot read/delete failures.
// Name + message only — never job IDs, snapshot contents or PII.
const SNAPSHOT_DIAG_WINDOW_MS = 60_000;
const snapshotDiagLast = new Map<string, number>();
function reportSnapshotFailure(operation: string, err: unknown): void {
  try {
    const { error_name, error_message } = describeDbError(err);
    const key = `${operation}:${error_name}:${error_message}`;
    const now = Date.now();
    const last = snapshotDiagLast.get(key);
    if (last !== undefined && now - last < SNAPSHOT_DIAG_WINDOW_MS) return;
    snapshotDiagLast.set(key, now);
    captureEvent({
      kind: "error",
      severity: "warn",
      component: "offline.snapshot_db",
      message: "snapshot_db_operation_failed",
      metadata: { operation, error_name, error_message },
    });
  } catch { /* telemetry must never break callers */ }
}

/** Test helper. */
export function __resetSnapshotDiagnostics(): void { snapshotDiagLast.clear(); }

export interface JobSnapshot {
  job_id: string;
  job: any;
  customer: any | null;
  /** Additive (Phase 13.8): contacts for the job's customer */
  customerContacts?: any[];
  existingAppraisals: any[];
  vehiclePanelAnchors: any[];
  snapshotAt: number;
  version: number;
}

const SNAPSHOT_STORE = "job_snapshots";
const SNAPSHOT_DB_NAME = "transportmate_snapshots";
const SNAPSHOT_DB_VERSION = 1;

let snapshotDb: IDBDatabase | null = null;
let snapshotOpening: Promise<IDBDatabase> | null = null;

/**
 * Drop a stale snapshot connection so the next call reopens. Identity-checked:
 * an event or failure on an OLD handle never resets a newer, healthy one.
 * Never deletes, clears or upgrades the database.
 */
function invalidateSnapshotConnection(db: IDBDatabase): void {
  if (snapshotDb !== db) return;
  snapshotDb = null;
  snapshotOpening = null;
  try { db.close(); } catch { /* already dead */ }
}

function openSnapshotDb(): Promise<IDBDatabase> {
  if (snapshotDb) return Promise.resolve(snapshotDb);
  if (snapshotOpening) return snapshotOpening;
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(SNAPSHOT_DB_NAME, SNAPSHOT_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
        db.createObjectStore(SNAPSHOT_STORE, { keyPath: "job_id" });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onclose = () => invalidateSnapshotConnection(db);
      db.onversionchange = () => invalidateSnapshotConnection(db);
      if (snapshotOpening === opening) { snapshotDb = db; snapshotOpening = null; }
      resolve(db);
    };
    req.onerror = () => {
      if (snapshotOpening === opening) snapshotOpening = null;
      reject(req.error);
    };
  });
  snapshotOpening = opening;
  return opening;
}

/**
 * Run a snapshot DB operation; on a recoverable (stale/lost-transaction) error,
 * drop that handle and retry exactly once on a shared fresh connection.
 * Non-recoverable errors and failed retries are rethrown to the caller's
 * existing catch/fallback. Deliberately separate from offlineDb `withDb()` so
 * snapshot failures never touch the shared storage-recovery pause.
 */
async function withSnapshotDb<T>(fn: (db: IDBDatabase) => Promise<T>): Promise<T> {
  const db = await openSnapshotDb();
  try {
    return await fn(db);
  } catch (err) {
    if (!isRecoverableDbError(err)) throw err;
    invalidateSnapshotConnection(db);
    const fresh = await openSnapshotDb();
    try {
      return await fn(fresh);
    } catch (retryErr) {
      if (isRecoverableDbError(retryErr)) invalidateSnapshotConnection(fresh);
      throw retryErr;
    }
  }
}

/** Test helper. */
export function __resetSnapshotConnection(): void { snapshotDb = null; snapshotOpening = null; }

async function putSnapshot(snapshot: JobSnapshot): Promise<void> {
  return withSnapshotDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SNAPSHOT_STORE, "readwrite");
    tx.objectStore(SNAPSHOT_STORE).put(snapshot);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

export async function getSnapshot(jobId: string): Promise<JobSnapshot | null> {
  try {
    return await withSnapshotDb((db) => new Promise<JobSnapshot | null>((resolve, reject) => {
      const tx = db.transaction(SNAPSHOT_STORE, "readonly");
      const req = tx.objectStore(SNAPSHOT_STORE).get(jobId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    }));
  } catch (err) {
    reportSnapshotFailure("getSnapshot", err);
    return null;
  }
}

export async function getAllSnapshots(): Promise<JobSnapshot[]> {
  try {
    return await withSnapshotDb((db) => new Promise<JobSnapshot[]>((resolve, reject) => {
      const tx = db.transaction(SNAPSHOT_STORE, "readonly");
      const req = tx.objectStore(SNAPSHOT_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    }));
  } catch (err) {
    reportSnapshotFailure("getAllSnapshots", err);
    return [];
  }
}

export async function deleteSnapshot(jobId: string): Promise<void> {
  try {
    await withSnapshotDb((db) => new Promise<void>((resolve, reject) => {
      const tx = db.transaction(SNAPSHOT_STORE, "readwrite");
      tx.objectStore(SNAPSHOT_STORE).delete(jobId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    }));
  } catch (err) {
    reportSnapshotFailure("deleteSnapshot", err);
  }
}

export type SnapshotStatus = "available" | "partial" | "not_available";

export function getSnapshotStatus(snapshot: JobSnapshot | null): SnapshotStatus {
  if (!snapshot) return "not_available";
  // Snapshot older than 7 days = partial (stale)
  const age = Date.now() - snapshot.snapshotAt;
  if (age > 7 * 24 * 60 * 60 * 1000) return "partial";
  return "available";
}

/**
 * Download a complete offline snapshot for a single job.
 * Returns the snapshot or throws on network error.
 */
export async function downloadJobSnapshot(jobId: string): Promise<JobSnapshot> {
  // 1. Fetch job details
  const { data: job, error: jobErr } = await supabase
    .from("jobs")
    .select("*, customers(*)")
    .eq("id", jobId)
    .single();
  if (jobErr || !job) throw new Error(jobErr?.message || "Job not found");

  // 2. Fetch existing appraisals for this job
  const { data: appraisals } = await supabase
    .from("vehicle_appraisals")
    .select("*")
    .eq("job_id", jobId);

  // 3. Fetch vehicle panel anchors (reference data for appraisal UI)
  const { data: anchors } = await supabase
    .from("vehicle_panel_anchors")
    .select("*");

  // 3b. Customer contacts (phone numbers the driver may need offline)
  let customerContacts: any[] = [];
  if (job.customer_id) {
    const { data: contacts } = await supabase
      .from("customer_contacts")
      .select("*")
      .eq("customer_id", job.customer_id);
    customerContacts = contacts || [];
  }

  // 4. Build snapshot
  const snapshot: JobSnapshot = {
    job_id: jobId,
    job,
    customer: job.customers || null,
    customerContacts,

    existingAppraisals: appraisals || [],
    vehiclePanelAnchors: anchors || [],
    snapshotAt: Date.now(),
    version: 1,
  };

  // 5. Store in IndexedDB
  await putSnapshot(snapshot);

  // 6. Also update jobs_cache for dashboard offline access
  const cached: CachedJob = {
    job_id: job.id,
    company_id: job.company_id || "",
    assigned_user_id: job.driver_id || "",
    payload_json: JSON.stringify(job),
    updated_at: Date.now(),
  };
  await jobsCache.put(cached);

  console.log(`[SnapshotManager] Downloaded snapshot for job ${jobId}`);
  return snapshot;
}

/**
 * Download snapshots for all assigned jobs (batch prefetch).
 */
export async function downloadAllAssignedSnapshots(userId: string): Promise<number> {
  const { data: drivers } = await supabase
    .from("drivers")
    .select("id")
    .eq("user_id", userId);

  if (!drivers || drivers.length === 0) return 0;

  const driverIds = drivers.map(d => d.id);

  const { data: jobs } = await supabase
    .from("jobs")
    .select("id")
    .in("driver_id", driverIds)
    .in("status", ["pending", "in_progress"])
    .is("completed_at", null);

  if (!jobs || jobs.length === 0) return 0;

  let count = 0;
  for (const job of jobs) {
    try {
      await downloadJobSnapshot(job.id);
      count++;
    } catch (err) {
      console.warn(`[SnapshotManager] Failed to snapshot job ${job.id}:`, err);
    }
  }

  await appState.setNonEssential("last_snapshot_sync", String(Date.now()));
  console.log(`[SnapshotManager] Downloaded ${count}/${jobs.length} snapshots`);
  return count;
}
